import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { BRAND_PURPLE, GarlicMark as Mark } from '../ui/primitives';
import { ArrowRight, BarChart3, Check, ChefHat, CreditCard, Printer, QrCode, ShieldCheck, Star, Users } from 'lucide-react';

/** Contato comercial (provisório, informado pelo dono em 30/10/2026). */
const WHATSAPP = '5541987129316';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent('Olá! Quero conhecer o Garlic para o meu restaurante.')}`;

/** Roxo da marca (o do ícone oficial). */
const PURPLE = BRAND_PURPLE;

function Wordmark({ light = false }: { light?: boolean }) {
  return (
    <span className="inline-flex items-center gap-3">
      <Mark className={`size-10 ${light ? 'rounded-[22%] ring-2 ring-white/30' : ''}`} />
      <span
        className={`font-display text-[1.75rem] font-extrabold leading-none tracking-[-0.03em] ${light ? 'text-white' : 'text-ink'}`}
        style={{ fontVariationSettings: "'SOFT' 0, 'WONK' 0" }}
      >
        garlic
      </span>
    </span>
  );
}

function WhatsAppIcon({ className = 'size-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.12-.27-.2-.57-.35zM12.04 21.5h-.01a9.4 9.4 0 0 1-4.8-1.32l-.34-.2-3.57.94.95-3.48-.22-.36a9.43 9.43 0 1 1 7.99 4.42zm8.02-17.46A11.27 11.27 0 0 0 12.04.75C5.8.75.72 5.83.72 12.07c0 2 .52 3.94 1.51 5.65L.62 23.25l5.67-1.49a11.3 11.3 0 0 0 5.75 1.47h.01c6.24 0 11.32-5.08 11.32-11.32 0-3.02-1.18-5.87-3.31-8z" />
    </svg>
  );
}

function WhatsAppButton({ children, size = 'lg' }: { children: ReactNode; size?: 'md' | 'lg' }) {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`group inline-flex items-center justify-center gap-2.5 rounded-2xl bg-[#25d366] font-semibold text-[#0b2a17] shadow-[0_10px_22px_-12px_rgb(10_4_16/0.6)] transition-[transform,box-shadow,background-color] duration-200 ease-out hover:-translate-y-0.5 hover:bg-[#2be070] hover:shadow-[0_16px_28px_-14px_rgb(10_4_16/0.65)] active:translate-y-0 ${size === 'lg' ? 'h-14 px-7 text-base' : 'h-11 px-5 text-sm'}`}
    >
      <WhatsAppIcon className={size === 'lg' ? 'size-6' : 'size-5'} />
      {children}
    </a>
  );
}

/* ───────────────────────── Telas do produto (ilustrativas) ───────────────────────── */

function Phone({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-[2.6rem] bg-[#16101c] p-2.5 shadow-[0_40px_80px_-30px_rgb(20_6_32/0.65)] ${className}`}>
      <div className="relative overflow-hidden rounded-[2.1rem] bg-white">
        <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-[#16101c]" />
        {children}
      </div>
    </div>
  );
}

const menuItems = [
  { name: 'Pão de alho', desc: 'Manteiga de alho assado', price: '19,00' },
  { name: 'Hambúrguer', desc: '180 g, queijo e cebola', price: '32,00' },
  { name: 'Risoto', desc: 'Shimeji e parmesão', price: '48,00' },
];

function MenuScreen() {
  return (
    <div className="pb-5 pt-10 text-left">
      <div className="px-5">
        <p className="text-[12px] font-medium text-[#6b5a77]">Mesa 7 · Bistrô Exemplo</p>
        <p className="mt-1 font-display text-xl font-bold leading-tight text-ink">O que vai ser hoje?</p>
      </div>
      <div className="mt-3 flex gap-1.5 px-5 text-[12px] font-medium">
        {['Entradas', 'Pratos', 'Bebidas'].map((c, i) => (
          <span key={c} className={i === 0 ? 'rounded-full px-3 py-1 text-white' : 'rounded-full bg-[#f3eef8] px-3 py-1 text-[#4c3a5b]'} style={i === 0 ? { background: PURPLE } : undefined}>
            {c}
          </span>
        ))}
      </div>
      <ul className="mt-3 space-y-2 px-4">
        {menuItems.map((m, i) => (
          <li key={m.name} className="flex items-center gap-3 rounded-2xl border border-[#efe8f4] p-2.5">
            <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-[#f6f0fa] text-lg font-bold" style={{ color: PURPLE }}>
              {m.name[0]}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold leading-tight text-ink">{m.name}</span>
              <span className="block text-[12px] leading-tight text-[#7a6e84]">{m.desc}</span>
            </span>
            <span className="text-right">
              <span className="block text-[12px] font-semibold tabular-nums text-ink">R$ {m.price}</span>
              {i === 1 && <span className="mt-0.5 inline-block rounded-md bg-[#25d366]/15 px-1.5 text-[12px] font-semibold text-[#137a3a]">no pedido</span>}
            </span>
          </li>
        ))}
      </ul>
      <div className="mx-4 mt-4 flex items-center justify-between rounded-2xl px-4 py-3 text-white" style={{ background: PURPLE }}>
        <span className="text-[12px]">2 itens</span>
        <span className="text-[13px] font-semibold">Enviar pedido · R$ 51,00</span>
      </div>
    </div>
  );
}

function Ticket({ station, lines, rotate = 0, className = '' }: { station: string; lines: { q: string; item: string; note?: string }[]; rotate?: number; className?: string }) {
  return (
    <div className={`w-52 bg-white px-4 pb-5 pt-4 text-left text-ink shadow-[0_24px_50px_-20px_rgb(20_6_32/0.55)] ${className}`} style={{ transform: `rotate(${rotate}deg)` }}>
      <p className="flex items-center justify-between font-mono text-[12px] tracking-wide text-[#5d5266]">
        <span>{station}</span>
        <span>#0042 · MESA 7</span>
      </p>
      <div className="my-2.5 border-t border-dashed border-[#cfc6d6]" />
      {lines.map((l) => (
        <p key={l.item} className="mt-1.5 font-mono text-[13px] leading-snug">
          <b>{l.q}</b> {l.item}
          {l.note && <span className="block pl-5 text-[12px] text-[#5d5266]">» {l.note}</span>}
        </p>
      ))}
      <div className="my-2.5 border-t border-dashed border-[#cfc6d6]" />
      <p className="font-mono text-[12px] text-[#5d5266]">21:14 · impresso automaticamente</p>
    </div>
  );
}

function BillScreen() {
  return (
    <div className="px-5 pb-6 pt-11 text-left">
      <p className="text-[12px] font-medium text-[#6b5a77]">Conta da mesa 7</p>
      <p className="mt-1 font-display text-3xl font-bold tabular-nums text-ink">R$ 186,00</p>
      <div className="mt-3 grid grid-cols-3 gap-1 rounded-xl bg-[#f3eef8] p-1 text-center text-[12px] font-medium">
        <span className="rounded-lg py-1.5 text-[#4c3a5b]">Tudo</span>
        <span className="rounded-lg bg-white py-1.5 text-ink shadow-sm">Meus itens</span>
        <span className="rounded-lg py-1.5 text-[#4c3a5b]">Valor</span>
      </div>
      <ul className="mt-3 space-y-1.5 text-[12px]">
        {[
          ['1× Risoto de cogumelos', '48,00', true],
          ['1 de 3 · Cerveja long neck', '14,00', true],
          ['1× Pão de alho da casa', '19,00', false],
        ].map(([n, v, on]) => (
          <li key={n as string} className="flex items-center gap-2 rounded-xl border border-[#efe8f4] px-3 py-2">
            <span className={`grid size-4 place-items-center rounded ${on ? 'text-white' : 'border border-[#cfc6d6]'}`} style={on ? { background: PURPLE } : undefined}>
              {on && <Check className="size-3" strokeWidth={3} />}
            </span>
            <span className="flex-1 text-ink">{n}</span>
            <span className="tabular-nums text-[#4c3a5b]">R$ {v}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 rounded-2xl bg-[#e9fbf0] p-3 text-[12px] text-[#0e5a2b]">
        <p className="font-semibold">Pix aprovado · R$ 62,00</p>
        <p className="mt-0.5 text-[12px]">Direto na conta Mercado Pago do restaurante</p>
      </div>
    </div>
  );
}

/** QR real (do código de exemplo), como o comprovante de verdade. */
function DemoQr({ value }: { value: string }) {
  const [svg, setSvg] = useState('');
  useEffect(() => {
    void QRCode.toString(value, { type: 'svg', margin: 0, errorCorrectionLevel: 'M', color: { dark: '#1f1a17', light: '#ffffff' } }).then(setSvg);
  }, [value]);
  return <div className="mx-auto mt-3 size-28 rounded-xl border border-[#efe8f4] p-2 [&_svg]:size-full" aria-hidden="true" dangerouslySetInnerHTML={{ __html: svg }} />;
}

function ReceiptScreen() {
  return (
    <div className="px-5 pb-6 pt-11 text-center">
      <p className="inline-flex items-center gap-1.5 font-display text-xl font-bold text-[#2f7d12]">
        <Check className="size-5" strokeWidth={3} /> CONTA PAGA
      </p>
      <DemoQr value="K7MPQ4XR2A" />
      <p className="mt-2 font-mono text-[15px] font-semibold tracking-[0.2em] text-ink">K7MPQ-4XR2A</p>
      <p className="text-[12px] text-[#6b5a77]">Mostre ao balconista na saída</p>
      <div className="mt-4 rounded-2xl p-3 text-white" style={{ background: PURPLE }}>
        <p className="text-[12px] font-semibold">Gostou? Avalie a gente no Google</p>
        <span className="mt-2 inline-flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-[12px] font-semibold" style={{ color: PURPLE }}>
          <Star className="size-3.5 fill-current" /> Avaliar
        </span>
      </div>
    </div>
  );
}

/* ───────────────────────── Como funciona (interação principal) ───────────────────────── */

const steps = [
  {
    title: 'O cliente pede pelo celular',
    text: 'Escaneia o QR Code da mesa, escolhe, personaliza e envia. Sem baixar app, sem criar conta. Garçons também lançam pedidos pelo painel.',
    icon: QrCode,
    screen: (
      <Phone className="w-[19rem]">
        <MenuScreen />
      </Phone>
    ),
  },
  {
    title: 'Cada item vai para o setor certo',
    text: 'O pedido se divide sozinho: o que é da cozinha sai na impressora da cozinha, o que é do bar sai no bar. Tela de produção é opcional.',
    icon: Printer,
    screen: (
      <div className="relative h-[22rem] w-[19rem]">
        <Ticket station="COZINHA" rotate={-4} className="absolute left-0 top-4" lines={[{ q: '1×', item: 'Hambúrguer artesanal', note: 'ao ponto, sem cebola' }, { q: '1×', item: 'Pão de alho da casa' }]} />
        <Ticket station="BAR" rotate={5} className="absolute right-0 top-28" lines={[{ q: '3×', item: 'Cerveja long neck' }, { q: '1×', item: 'Suco de laranja', note: 'sem gelo' }]} />
      </div>
    ),
  },
  {
    title: 'A conta se divide sem confusão',
    text: 'Cada um paga tudo, só os seus itens (até parte de um item) ou um valor livre. Pix e cartão caem direto na conta Mercado Pago do restaurante.',
    icon: CreditCard,
    screen: (
      <Phone className="w-[19rem]">
        <BillScreen />
      </Phone>
    ),
  },
  {
    title: 'Saída conferida, avaliação no Google',
    text: 'Quem pagou recebe um comprovante com código, validado no balcão em segundos. E cada cliente é convidado a avaliar o restaurante no Google.',
    icon: Star,
    screen: (
      <Phone className="w-[19rem]">
        <ReceiptScreen />
      </Phone>
    ),
  },
];

function HowItWorks() {
  const [active, setActive] = useState(0);
  const refs = useRef<(HTMLLIElement | null)[]>([]);

  // No desktop, a tela ao lado acompanha o passo que está no meio da tela.
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) setActive(Number((e.target as HTMLElement).dataset.step));
      },
      { rootMargin: '-45% 0px -45% 0px' },
    );
    refs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section id="como-funciona" className="scroll-mt-24 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
        <h2 className="max-w-2xl font-display text-4xl font-bold leading-[1.05] tracking-[-0.03em] text-ink [text-wrap:balance] sm:text-5xl">Do pedido à porta, em quatro passos</h2>
        <p className="mt-4 max-w-xl text-lg text-ink-2">Tudo conectado: o que o cliente pede chega à cozinha, entra na conta e é conferido na saída.</p>

        <div className="mt-14 grid gap-12 lg:grid-cols-[1fr_minmax(0,26rem)] lg:gap-16">
          <ol className="space-y-6 lg:space-y-0">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const on = active === i;
              return (
                <li
                  key={s.title}
                  ref={(el) => {
                    refs.current[i] = el;
                  }}
                  data-step={i}
                  className="lg:flex lg:min-h-[52vh] lg:items-center"
                >
                  <button
                    type="button"
                    onClick={() => setActive(i)}
                    className={`w-full rounded-3xl p-6 text-left transition-colors duration-300 sm:p-8 ${on ? 'bg-[#f6f0fa]' : 'lg:hover:bg-[#faf7fc]'}`}
                  >
                    <span className="flex items-center gap-3">
                      <span className={`grid size-11 place-items-center rounded-2xl transition-colors duration-300 ${on ? 'text-white' : 'bg-[#f3eef8]'}`} style={on ? { background: PURPLE } : { color: PURPLE }}>
                        <Icon className="size-5" aria-hidden />
                      </span>
                      <span className="font-display text-2xl font-bold tracking-[-0.02em] text-ink sm:text-[1.75rem]">{s.title}</span>
                    </span>
                    <span className="mt-3 block max-w-[34rem] text-base leading-relaxed text-ink-2">{s.text}</span>
                  </button>
                  {/* No celular, cada passo mostra a sua própria tela logo abaixo. */}
                  <div className="mt-6 flex justify-center lg:hidden">{s.screen}</div>
                </li>
              );
            })}
          </ol>

          <div className="relative hidden lg:block">
            <div className="sticky top-24 flex h-[36rem] items-center justify-center">
              {steps.map((s, i) => (
                <div
                  key={s.title}
                  aria-hidden={active !== i}
                  className={`absolute inset-0 flex items-center justify-center transition-[opacity,transform,filter] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${active === i ? 'opacity-100 [transform:none] [filter:none]' : 'pointer-events-none translate-y-6 scale-[0.97] opacity-0 [filter:blur(6px)]'}`}
                >
                  {s.screen}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────────────────────── Página ───────────────────────── */

const operation = [
  { icon: Printer, title: 'Impressora térmica ou tela, você escolhe', text: 'Cada setor recebe as comandas do jeito que funciona na sua casa. Se a impressora parar, o painel avisa na hora.' },
  { icon: ShieldCheck, title: 'Seu dinheiro na sua conta', text: 'Pix e cartão caem direto no Mercado Pago do restaurante. O Garlic não guarda nenhum dado de cartão.' },
  { icon: Users, title: 'Equipe com permissões', text: 'Garçom, cozinha, bar e caixa veem só o que precisam. Cada ação sensível fica registrada.' },
  { icon: BarChart3, title: 'Relatórios que fecham o caixa', text: 'Vendas por dia, produto e setor, formas de pagamento, cancelamentos e descontos, prontos para exportar.' },
];

/** Cabeçalho fixo: roxo, com o WhatsApp sempre à mão; ganha sombra ao rolar. */
function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  return (
    <header
      className={`fixed inset-x-0 top-0 z-30 transition-shadow duration-300 [&_:focus-visible]:outline-white! ${scrolled ? 'shadow-[0_8px_24px_-12px_rgb(10_4_16/0.5)]' : ''}`}
      style={{ background: PURPLE }}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3.5 sm:px-6">
        <Link to="/" aria-label="Garlic — página inicial">
          <Wordmark light />
        </Link>
        <nav className="flex items-center gap-1 text-sm font-medium text-white/85">
          <a href="#como-funciona" className="hidden rounded-xl px-3 py-2 hover:bg-white/10 hover:text-white md:inline-block">
            Como funciona
          </a>
          <a href="#recursos" className="hidden rounded-xl px-3 py-2 hover:bg-white/10 hover:text-white md:inline-block">
            Recursos
          </a>
          <Link to="/painel" className="whitespace-nowrap rounded-xl px-3 py-2 hover:bg-white/10 hover:text-white">
            Área do restaurante
          </Link>
        </nav>
      </div>
    </header>
  );
}

export function Landing() {
  return (
    <div className="min-h-dvh bg-white text-ink selection:bg-[#e3d2f0] selection:text-[#2a0a40]">
      <SiteHeader />

      <main>
        {/* Abertura: promessa, ação e o produto funcionando. */}
        <section className="relative overflow-hidden text-white [&_:focus-visible]:outline-white!" style={{ background: PURPLE }}>
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 pb-20 pt-24 sm:gap-14 sm:px-6 sm:pt-32 lg:grid-cols-[1.15fr_1fr] lg:pb-28 lg:pt-36">
            <div>
              <h1 className="font-display text-[2.5rem] font-extrabold leading-[1.02] tracking-[-0.035em] [text-wrap:balance] sm:text-6xl lg:text-[3.6rem]">
                Do pedido na mesa à saída pela porta. <span className="block sm:whitespace-nowrap">Tudo num só lugar.</span>
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-[#ead9f6] sm:mt-6 sm:text-xl">
                Garlic organiza cardápio, pedidos, cozinha, bar, impressão e pagamentos do seu restaurante. Todo o fluxo do seu negócio na palma de suas mãos!
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-1 sm:mt-9">
                <WhatsAppButton>Falar no WhatsApp</WhatsAppButton>
                <a href="#como-funciona" className="group inline-flex h-12 items-center gap-2 rounded-2xl px-2 font-semibold text-white transition-colors hover:bg-white/10 sm:h-14 sm:px-5 sm:ring-1 sm:ring-white/30">
                  Ver como funciona <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </a>
              </div>
              <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#ead9f6]">
                {['Sem app para o cliente', 'Pix e cartão na sua conta', 'Funciona com impressora térmica'].map((t) => (
                  <li key={t} className="inline-flex items-center gap-2">
                    <Check className="size-4 text-[#7ef0a8]" strokeWidth={3} aria-hidden /> {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mx-auto h-[37.75rem] w-full max-w-[32rem] sm:h-[37rem]" aria-hidden="true">
              <Phone className="absolute left-1/2 top-0 w-[17.5rem] -translate-x-[62%] sm:left-0 sm:w-[19rem] sm:translate-x-0">
                <MenuScreen />
              </Phone>
              <Ticket station="COZINHA" rotate={6} className="absolute bottom-0 right-0 origin-bottom-right scale-[0.72] sm:scale-100" lines={[{ q: '1×', item: 'Hambúrguer artesanal', note: 'ao ponto, sem cebola' }, { q: '1×', item: 'Pão de alho da casa' }]} />
              <div className="absolute right-0 top-16 hidden items-center gap-3 rounded-2xl bg-white px-4 py-3 text-left text-ink shadow-[0_20px_40px_-18px_rgb(20_6_32/0.6)] sm:flex">
                <span className="grid size-9 place-items-center rounded-xl bg-[#e9fbf0] text-[#137a3a]">
                  <Check className="size-5" strokeWidth={3} />
                </span>
                <span>
                  <span className="block text-sm font-semibold">Pix aprovado</span>
                  <span className="block text-xs text-ink-3">Mesa 7 · R$ 62,00</span>
                </span>
              </div>
            </div>
          </div>
        </section>

        <HowItWorks />

        {/* Avaliações no Google: o convite de verdade, como o cliente vê. */}
        <section className="overflow-hidden bg-[#f7f2fb]">
          <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_auto] lg:py-24">
            <div>
              <h2 className="max-w-2xl font-display text-4xl font-bold leading-[1.05] tracking-[-0.03em] text-ink [text-wrap:balance] sm:text-5xl">Mais avaliações no Google, sem pedir uma por uma</h2>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-2 [text-wrap:pretty]">
                Na hora em que a conta é paga, o comprovante convida o cliente a avaliar o seu restaurante no Google com um toque. Cada cliente que paga recebe o convite, todo dia, sem a equipe precisar lembrar.
              </p>
            </div>
            <div className="flex justify-center">
              <Phone className="w-[17.5rem]">
                <ReceiptScreen />
              </Phone>
            </div>
          </div>
        </section>

        {/* Operação: o que o dono e a equipe usam no dia a dia. */}
        <section id="recursos" className="scroll-mt-24 bg-white">
          <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
            <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
              <div>
                <h2 className="font-display text-4xl font-bold leading-[1.05] tracking-[-0.03em] text-ink [text-wrap:balance] sm:text-5xl">Feito para a correria do salão</h2>
                <p className="mt-5 max-w-md text-lg leading-relaxed text-ink-2">
                  Com o seu nome e as suas cores diretamente na web. Funciona em todos os dispositivos!
                </p>
                <div className="mt-8 flex flex-wrap gap-3 text-sm font-medium text-ink-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-[#f6f0fa] px-4 py-2">
                    <ChefHat className="size-4" style={{ color: PURPLE }} /> Restaurantes, bares e lanchonetes
                  </span>
                </div>
              </div>
              <ul className="divide-y divide-[#ece6f1] border-y border-[#ece6f1]">
                {operation.map(({ icon: Icon, title, text }) => (
                  <li key={title} className="flex gap-5 py-7">
                    <Icon className="mt-1 size-6 shrink-0" style={{ color: PURPLE }} aria-hidden />
                    <div>
                      <h3 className="text-lg font-semibold text-ink">{title}</h3>
                      <p className="mt-1.5 leading-relaxed text-ink-2">{text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Fechamento: a mesma ação da abertura. */}
        <section className="px-4 pb-16 sm:px-6">
          <div className="relative mx-auto max-w-6xl overflow-hidden rounded-[2.5rem] px-6 py-16 text-center text-white [&_:focus-visible]:outline-white! sm:px-12 sm:py-20" style={{ background: PURPLE }}>
            <Mark className="mx-auto size-16 ring-4 ring-white/15 [border-radius:22%]" />
            <h2 className="mx-auto mt-6 max-w-2xl font-display text-4xl font-bold leading-[1.05] tracking-[-0.03em] [text-wrap:balance] sm:text-5xl">Vamos colocar o seu restaurante no Garlic?</h2>
            <p className="mx-auto mt-4 max-w-lg text-lg text-[#ead9f6]">Chame no WhatsApp e mostramos tudo funcionando com o seu cardápio.</p>
            <div className="mt-8 flex justify-center">
              <WhatsAppButton>Falar no WhatsApp</WhatsAppButton>
            </div>
            <p className="mt-4 text-sm text-[#d9c2ea]">(41) 98712-9316</p>
          </div>
        </section>
      </main>

      <footer className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 pb-10 text-sm text-ink-3 sm:px-6">
        <Wordmark />
        <span>© {new Date().getFullYear()} Garlic · Tecnologia para restaurantes · Telas ilustrativas</span>
        <span className="flex gap-4">
          <a href="mailto:contato@garlicfood.com.br" className="hover:text-ink">contato@garlicfood.com.br</a>
          <Link to="/painel" className="hover:text-ink">Área do restaurante</Link>
        </span>
      </footer>
    </div>
  );
}
